import { OPTIONS_PER_ROUND } from './sampleSlots';

/**
 * Photos held in the browser while a submission is being written.
 *
 * The dialog asks for a title, notes and photos for four components, and sends
 * the lot on one button. The database will not take it in that order: a photo
 * is a row in submission_options with a NOT NULL foreign key to the
 * submission, and guard_option_insert reads the parent's status before
 * allowing the insert. No parent, no photo.
 *
 * So the files wait here, in memory, until the submission row exists — and
 * then go up against its id. That gap is the whole reason this file has states
 * rather than a plain Record<string, File[]>: once the row is written, a retry
 * must upload only what has not landed yet. Re-sending an 'uploaded' option
 * would put two copies of the same charm into a component capped at five.
 *
 * ── Five per component, not one ────────────────────────────────────────────
 *
 * TAP picks a winner from what a factory sends. One photo per component gives
 * the reviewer nothing to choose between, which is what the first version of
 * this dialog quietly enforced. A component takes up to five options and the
 * database says so — guard_option_insert raises
 * too_many_options_for_component on the sixth — so the cap here is imported
 * from sampleSlots rather than written again. Two constants would be two
 * places to change, and the one that did not change would offer a sixth
 * upload that could only be refused.
 *
 * There is no cleanup path if the person walks away mid-upload.
 * supplier_submissions has no DELETE grant and no DELETE policy, by design — a
 * supplier cannot withdraw a run TAP may already have started reading. The
 * dialog says so instead of pretending otherwise.
 */

export const MAX_OPTIONS_PER_COMPONENT = OPTIONS_PER_ROUND;

export type StagedOptionState = 'ready' | 'uploading' | 'uploaded' | 'failed';

export interface StagedOption {
  /** Stable across reordering and removal, so React keys and retries hold. */
  id: string;
  file: File;
  state: StagedOptionState;
  /** Why the last attempt failed, already translated for a human. */
  error: string;
}

/** Component name to its options, in the order they were added. */
export type StagedMap = Record<string, StagedOption[]>;

export interface StagedSlot {
  component: string;
  options: StagedOption[];
  canAddMore: boolean;
  /** Wording for the add control, or null when the round is full. */
  addLabel: string | null;
}

/**
 * The add control's text.
 *
 * "Choose photo" for the first one, because there is nothing to add to yet —
 * "+ Add Option 1" would be describing a list that does not exist. The
 * numbering starts once a second option is actually possible, which is also
 * the moment it starts telling the supplier something they did not know: that
 * a component takes more than one.
 */
export function addLabel(count: number): string | null {
  if (count >= MAX_OPTIONS_PER_COMPONENT) {
    return null;
  }
  return count === 0 ? 'Choose photo' : `+ Add Option ${String(count + 1)}`;
}

/**
 * The four slots in the order the component table gives them, whatever the map
 * happens to hold. Driving the list from the components rather than the map is
 * what keeps the dialog showing all four before anything is chosen.
 */
export function buildStagedSlots(components: string[], map: StagedMap): StagedSlot[] {
  return components.map((component) => {
    const options = map[component] ?? [];
    return {
      component,
      options,
      canAddMore: options.length < MAX_OPTIONS_PER_COMPONENT,
      addLabel: addLabel(options.length),
    };
  });
}

/**
 * Attach another option. Refused once the component holds five — the cap is
 * per component, not per submission, so a full Charm does not close the
 * Bracelet Cord.
 */
export function addOption(map: StagedMap, component: string, file: File, id: string): StagedMap {
  const options = map[component] ?? [];
  if (options.length >= MAX_OPTIONS_PER_COMPONENT) {
    return map;
  }
  return { ...map, [component]: [...options, { id, file, state: 'ready', error: '' }] };
}

/**
 * Take an option back off, before it has gone anywhere.
 *
 * An uploaded one stays: the row is in the database, this dialog has no way to
 * delete it, and removing it from the list would only misreport what TAP can
 * see. Removing a staged one frees the slot, so a fifth choice can be swapped
 * for a different fifth choice.
 */
export function removeOption(map: StagedMap, component: string, id: string): StagedMap {
  const options = map[component] ?? [];
  const target = options.find((option) => option.id === id);
  if (target === undefined || target.state === 'uploaded') {
    return map;
  }
  return { ...map, [component]: options.filter((option) => option.id !== id) };
}

export function markOption(
  map: StagedMap,
  component: string,
  id: string,
  state: StagedOptionState,
  error = '',
): StagedMap {
  const options = map[component] ?? [];
  return {
    ...map,
    [component]: options.map((option) => (option.id === id ? { ...option, state, error } : option)),
  };
}

export interface PendingUpload {
  component: string;
  option: StagedOption;
}

/**
 * What still has to go up: waiting, or failed and worth another go.
 *
 * Flattened in slot order and then option order, because that is the order the
 * database will number them in — it derives an option's place from when the
 * row arrives. Uploading out of order would show the reviewer somebody's
 * Option 1 as their Option 3.
 *
 * 'uploaded' is deliberately absent, and that omission is the load-bearing
 * part of this file: a retry that re-walked every staged option would
 * duplicate everything that succeeded on the first pass.
 */
export function pendingUploads(slots: StagedSlot[]): PendingUpload[] {
  const pending: PendingUpload[] = [];
  for (const slot of slots) {
    for (const option of slot.options) {
      if (option.state === 'ready' || option.state === 'failed') {
        pending.push({ component: slot.component, option });
      }
    }
  }
  return pending;
}

function countWhere(slots: StagedSlot[], state: StagedOptionState): number {
  return slots.reduce(
    (total, slot) => total + slot.options.filter((option) => option.state === state).length,
    0,
  );
}

export function uploadedCount(slots: StagedSlot[]): number {
  return countWhere(slots, 'uploaded');
}

export function readyCount(slots: StagedSlot[]): number {
  return countWhere(slots, 'ready');
}

export function failedOptions(slots: StagedSlot[]): PendingUpload[] {
  return slots.flatMap((slot) =>
    slot.options
      .filter((option) => option.state === 'failed')
      .map((option) => ({ component: slot.component, option })),
  );
}

/**
 * The primary button's wording, which has to say what will actually happen.
 *
 * Three different things can happen on that click depending on where the run
 * got to, and a button that read "Submit to TAP" after the submission had
 * already been written would be inviting somebody to send it twice.
 */
export function submitLabel(slots: StagedSlot[], created: boolean, busy: boolean): string {
  if (busy) {
    return 'Sending…';
  }
  if (!created) {
    const ready = readyCount(slots);
    if (ready === 0) {
      return 'Submit to TAP';
    }
    return `Submit to TAP (${String(ready)} ${ready === 1 ? 'photo' : 'photos'})`;
  }
  return failedOptions(slots).length === 0 ? 'Done' : 'Retry failed uploads';
}
